import { useToast as useDesignToast } from "@asap/design";
import * as Sentry from "@sentry/nuxt";
import { getServerErrorMessage } from "~/utils/error";
import { logger } from "~/utils/logger";

export type Toast = {
  id?: string | number;
  title?: string;
  description?: string;
  action?: {
    btnText: string;
    altText?: string;
    trigger: () => Promise<void> | void;
  };
  duration?: number;
  type?: "success" | "danger" | "error" | "warning" | "info";
};

const colorMap = {
  success: "success",
  warning: "warning",
  error: "critical",
  danger: "critical",
  info: "info",
} as const;

const iconMap = {
  success: "i-ph-check-circle-fill",
  warning: "i-ph-warning-fill",
  error: "i-ph-warning-circle-fill",
  danger: "i-ph-warning-circle-fill",
  info: "i-ph-warning-circle-fill",
} as const;

export const useToast = function () {
  const { add, remove, toasts } = useDesignToast();

  const addToast = (notification: Toast) => {
    const { type = "success", title, description, duration, action } = notification;

    const text = [title, description].filter((text) => text).join(" ");

    add({
      text,
      color: colorMap[type],
      icon: iconMap[type],
      duration,
      actions: action
        ? [
            {
              label: action.btnText,
              onClick: () => {
                action.trigger();
              },
            },
          ]
        : undefined,
    });
  };

  const addToastSuccess = (options: Toast) => {
    const { description, title, duration, action } = options;
    addToast({
      title: title ?? "Succès !",
      description: description ?? "Opération réussie.",
      type: "success",
      duration,
      action,
    });
  };

  const addToastWarning = (options: Toast) => {
    const { description, title, ...otherOptions } = options;
    addToast({
      ...otherOptions,
      title: title ?? "Attention !",
      description: description ?? "Vous êtes sur le point d'effectuer une action sensible.",
      type: "warning",
    });
  };

  const addToastError = <T>(options: Toast, error?: T) => {
    const { description, title, ...otherOptions } = options;
    if (error) logger.error(error);

    const errorToCapture =
      error instanceof Error ? error : new Error(typeof error === "object" ? JSON.stringify(error) : String(error));

    if (typeof error === "object" && error !== null) {
      Sentry.withScope((scope) => {
        scope.setExtra("originalError", error);
        Sentry.captureException(errorToCapture);
      });
    } else {
      Sentry.captureException(errorToCapture);
    }

    addToast({
      ...otherOptions,
      title: title ?? "Oops !",
      description: description ?? getServerErrorMessage(error) ?? "Une erreur est survenue.",
      type: "error",
    });
  };

  const addToastInfo = (options: Toast) => {
    const { title, ...otherOptions } = options;
    addToast({
      ...otherOptions,
      title: title ?? "Info",
      type: "info",
    });
  };

  const removeToast = (notification: Toast) => {
    if (notification.id !== undefined) {
      remove(notification.id);
    }
  };

  return {
    toasts,
    addToast,
    addToastError,
    addToastWarning,
    addToastSuccess,
    addToastInfo,
    remove: removeToast,
  };
};
